import React from 'react';
import { Row, Table, Typography } from 'antd';
import SealApprovalAttachmentsView from '../Admin/SealApprovalAttachmentsView';
import { resolveApplicantDeptDisplay, PrepaymentPayeeReadonly } from './PrepaymentReadonlyInfo';
import {
  FINANCE_READONLY_GUTTER,
  FinanceReadonlyItem,
  formatFinanceAmount,
  buildExpenseClaimLineRows,
  createExpenseLineTableColumns,
  countLineAttachments,
  resolvePrimaryBudgetCategoryName
} from './financeReadonlyUtils';
import './ExpenseClaimForm.css';

const { Text } = Typography;

export default function PrepaymentAppendClaimReadonlyView({ formData = {}, organizations = [] }) {
  const lines = buildExpenseClaimLineRows(formData.lines, organizations);
  const otherAttachments = formData.other_attachments || [];
  const applicantDeptName = resolveApplicantDeptDisplay(formData, organizations);
  const urgency = formData.urgency_label || (formData.urgency === 'urgent' ? '紧急' : '正常');
  const attachmentCount = formData.attachment_count ?? countLineAttachments(formData.lines);
  const lineColumns = createExpenseLineTableColumns();

  return (
    <div className="process-form-finance-grouped">
      {(formData.subject || formData.actual_pay_total != null) && (
        <div className="process-form-finance-summary">
          {formData.subject && (
            <div className="process-form-finance-summary-subject">{formData.subject}</div>
          )}
          <div className="process-form-finance-summary-grid">
            {formData.applicant_name && (
              <div className="process-form-finance-summary-item">
                <Text type="secondary">申请人</Text>
                <span>
                  {formData.applicant_name}
                  {applicantDeptName ? ` · ${applicantDeptName}` : ''}
                </span>
              </div>
            )}
            {formData.actual_pay_total != null && (
              <div className="process-form-finance-summary-item process-form-finance-summary-amount">
                <Text type="secondary">实付金额</Text>
                <span>{formatFinanceAmount(formData.actual_pay_total)}</span>
              </div>
            )}
            {formData.amount_uppercase && (
              <div className="process-form-finance-summary-item">
                <Text type="secondary">金额大写</Text>
                <span>{formData.amount_uppercase}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="prepayment-append-readonly-banner">
        <div>单据类型：{formData.doc_type || '费用报销-预付款追加'}</div>
        <div>预付款单号：{formData.prepayment_no || '—'}</div>
        {formData.prepayment_writeoff_no && (
          <div>核销单号：{formData.prepayment_writeoff_no}</div>
        )}
        <div>预算一级科目：{resolvePrimaryBudgetCategoryName(formData)}</div>
        {formData.append_amount != null && (
          <div>
            追加金额须等于：
            <strong> {formatFinanceAmount(formData.append_amount)}</strong>
          </div>
        )}
      </div>

      <section className="expense-claim-section">
        <div className="expense-claim-section-title">基本信息</div>
        <Row gutter={FINANCE_READONLY_GUTTER} className="prepayment-readonly-grid">
          <FinanceReadonlyItem label="申请人" value={formData.applicant_name} />
          <FinanceReadonlyItem label="申请人部门" value={applicantDeptName} />
          <FinanceReadonlyItem label="费用承担公司" value={formData.expense_company_name} />
          <FinanceReadonlyItem label="紧急程度" value={urgency} />
        </Row>
      </section>

      <section className="expense-claim-section">
        <div className="expense-claim-section-title">预算与事由</div>
        <Row gutter={FINANCE_READONLY_GUTTER} className="prepayment-readonly-grid">
          <FinanceReadonlyItem label="预算一级科目" value={resolvePrimaryBudgetCategoryName(formData)} />
          <FinanceReadonlyItem label="费用产生时间" value={formData.expense_occurrence_month} />
          <FinanceReadonlyItem label="所属预算周期" value={formData.budget_period_label} />
          <FinanceReadonlyItem label="事由" value={formData.reason} span={24} hideWhenEmpty />
        </Row>
      </section>

      <PrepaymentPayeeReadonly formData={formData} sectionSuffix="" context="exp_claim" />

      <section className="expense-claim-section">
        <div className="expense-claim-section-title">报销明细</div>
        {lines.length > 0 ? (
          <Table
            rowKey="key"
            size="small"
            columns={lineColumns}
            dataSource={lines}
            pagination={false}
            tableLayout="fixed"
            scroll={{ x: 820 }}
          />
        ) : (
          <div className="prepayment-readonly-value" style={{ whiteSpace: 'pre-wrap' }}>
            {formData.expense_lines_summary || '—'}
          </div>
        )}
      </section>

      <section className="expense-claim-section">
        <div className="expense-claim-section-title">金额汇总</div>
        <Row gutter={FINANCE_READONLY_GUTTER} className="prepayment-readonly-grid">
          <FinanceReadonlyItem label="发票金额合计" value={formatFinanceAmount(formData.invoice_total)} />
          <FinanceReadonlyItem label="实付金额合计" value={formatFinanceAmount(formData.actual_pay_total)} />
          <FinanceReadonlyItem label="附件张数" value={String(attachmentCount)} />
          {formData.payment_date && (
            <FinanceReadonlyItem label="付款日期" value={formData.payment_date} />
          )}
        </Row>
      </section>

      {(otherAttachments.length > 0 || formData.remark) && (
        <section className="expense-claim-section">
          <div className="expense-claim-section-title">备注与附件</div>
          {otherAttachments.length > 0 && (
            <div className="process-form-snapshot-attachments" style={{ marginBottom: 12 }}>
              <SealApprovalAttachmentsView attachments={otherAttachments} title="其他附件" />
            </div>
          )}
          {formData.remark && (
            <div className="prepayment-readonly-item">
              <Text type="secondary" className="prepayment-readonly-label">备注</Text>
              <div className="prepayment-readonly-value">{formData.remark}</div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
